/** @jsx jsx */
import { jsx } from '@emotion/react';
import React, { useCallback, useContext, useMemo } from 'react';
import { WorkspaceContext } from 'views/workspace/workspaceContext';
import { WorkspaceActionCreator } from 'hooks/useWorkspaceState';
import { SORT_VALUES, SORT_DIRECTIONS } from 'widgets/chartUtils';
import { CommonDropdownWithLabel } from 'components/dropdowns/common-dropdown-with-label';

/**
 * Selects a sort option which get set into a list.
 *
 * @param cardIndex
 * @param propertyName
 * @param label
 * @param endpointQuery
 * @param sortIndex
 * @param rest
 * @returns {JSX.Element}
 * @constructor
 */
export const EndpointSortListSelector = ({
	cardIndex,
	endpointQuery,
	propertyName = 'sortList',
	label = 'Sort By',
	sortIndex = 0,
	...rest
}) => {
	const { dispatch } = useContext(WorkspaceContext);

	const valueMap = useMemo(() => {
		const map = [];
		SORT_VALUES
			.filter(value => value.label !== 'Default')
			.forEach((value) => {
				// create a pairing of each value/direction
				SORT_DIRECTIONS.forEach((direction) => {
					const label = `${value.label} ${direction.label}`;
					map.push({
						sort: value.value,
						direction: direction.value,
						label: label,
					});
				});
		});
		return map;
	}, []);

	const handleSelectionChange = useCallback(
		(newVal) => {
			const configuration = JSON.parse(JSON.stringify(endpointQuery));
			if (newVal === null || newVal?.value === 'default' || newVal?.value === 'none') {
				if (sortIndex === 0) {
					delete configuration.options.parameters[propertyName];
				} else {
					delete configuration.options.parameters[propertyName]?.[sortIndex];
				}
			} else {
				if (!configuration.options.parameters[propertyName]) {
					configuration.options.parameters[propertyName] = [];
				}
				configuration.options.parameters[propertyName][sortIndex] = newVal;
			}
			dispatch(WorkspaceActionCreator.options(cardIndex, configuration));
		},
		[cardIndex, dispatch, endpointQuery, propertyName, sortIndex]
	);

	const activeValue = endpointQuery?.options?.parameters?.[propertyName]?.[sortIndex];
	const activeItem = activeValue ?? valueMap[0];
	const optionToString = (item) => item.label;

	return (
		<CommonDropdownWithLabel
			options={valueMap}
			// options={SORT_VALUES}
			onSelectionChanged={handleSelectionChange}
			activeItem={activeItem}
			optionToString={optionToString}
			label={label}
			btnText={optionToString(activeItem)}
			{...rest}
		/>
	);
};
